import {
  ActionIcon,
  ComboboxItem,
  Group,
  Stack,
  Text,
  FileButton,
  Button as MantineButton,
} from '@mantine/core';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { v4 as uuidv4 } from 'uuid';
import { GenericErrorPage } from 'src/error/GenericErrorPage';
import PageLoader from '../../UI/loader/PageLoader';
import { EntityWithFile, UploadedFile } from '../hooks/file-uploads/types';
import { useFileUploads } from '../hooks/file-uploads/useFileUploads';
import { useFileTemporaryLinkMutation } from '../hooks/useFileTemporaryLinkMutation';
import FilesTable from './files-table/FilesTable';
import FileStatusIcon from './FileStatusIcon';
import React, { useEffect, useMemo, useState } from 'react';
import { useDocumentAudioPlayer } from '../hooks/useDocumentAudioPlayer';
import { AudioPlayer } from 'src/meetings/detail/tabs/transcript/AudioPlayer';
import { useDetectedEntitiesStore } from '../stores/useDetectedEntitiesStore';
import { DropZone } from 'src/data-capture/file-management/drop-zone/DropZone';
import { PickedItem } from 'src/data-capture/file-management/drop-zone/SharePointButton';
import { getFileTypeLabel } from 'src/data-capture/utils/documentCategory';
import { Button } from 'src/components';
import { dataUploadFileProperties, Mixpanel } from 'src/tracking/mixpanel/Mixpanel';
import { MixpanelEvents } from 'src/tracking/mixpanel/MixpanelEvents';
import { IconArrowRight, IconFolder, IconPencil, IconPlus } from '@tabler/icons-react';
import MoveFileModal from './files-table/MoveFileModal';
import { useDisclosure } from '@mantine/hooks';
import FileUploadLayout from './FileUploadLayout';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { dispatchAppEvent } from 'src/common/events/appEvents';
import EmptyView from './components/EmptyView';
import { useOneDriveFileUpload } from '../hooks/file-uploads/useOneDriveFileUpload';
import { useSharePointPicker } from 'src/data-capture/file-management/drop-zone/SharePointProvider';
import { Logger } from 'src/utils/Logger';
import {
  isAudioFile,
  useAllowedFileTypes,
} from 'src/data-capture/file-management/allowedFileTypes';
import EditDateModal from './components/EditDateModal';
import { useUpdateFileDocumentDate } from '../hooks/useUpdateFileDocumentDate';
import { formatDate } from 'src/meetings/utils/date-utils';

const isPendingFile = (file: UploadedFile) => file.id.startsWith('pending-');

export function AddDocumentsPage() {
  const { clientId, client } = useCurrentClient();
  const { extensions, fileButtonAccept } = useAllowedFileTypes();
  const { openPicker, pendingPickedItems, clearPendingPickedItems } = useSharePointPicker();
  const clearExpiredFiles = useDetectedEntitiesStore(state => state.clearExpiredFiles);
  const [searchValue, setSearchValue] = useState('');
  const [selectedFileForMove, setSelectedFileForMove] = useState<UploadedFile | null>(null);
  const [moveModalOpened, { open: openMoveModal, close: closeMoveModal }] = useDisclosure(false);
  const [pendingFiles, setPendingFiles] = useState<UploadedFile[]>([]);

  useEffect(() => {
    if (pendingPickedItems && pendingPickedItems.length > 0) {
      Logger.info('Processing pending OneDrive items from global provider', { pendingPickedItems });
      uploadOneDriveFilesDirectly(pendingPickedItems);
      clearPendingPickedItems();
    }
  }, [pendingPickedItems, clearPendingPickedItems]);

  const selectData = [
    { value: 'BANK_STATEMENT', label: 'Bank statement' },
    { value: 'LETTER_OF_CORRESPONDENCE', label: 'Letter of correspondence' },
    { value: 'UTILITY_BILL', label: 'Utility bill' },
    { value: 'FACT_FIND', label: 'Fact find' },
    { value: 'IDENTIFICATION_DOCUMENT', label: 'Identification document' },
    { value: 'MEETING_NOTES', label: 'Meeting notes' },
    { value: 'AUDIO', label: 'Audio' },
  ];
  const [documentTypeChoice] = useState<ComboboxItem>(selectData[0]);

  useEffect(() => {
    clearExpiredFiles();
  }, []);

  const {
    files,
    filesToAdd,
    deleteFile,
    setFileResolved,
    refetchFiles,
    moveFile,
    isLoading: isLoadingFiles,
    error,
  } = useFileUploads({
    autoRefreshProcessing: true,
  });
  const isLoading = isLoadingFiles;

  const { uploadFiles } = useFileUploads();
  const { uploadOneDriveFiles } = useOneDriveFileUpload();

  const {
    createLink,
    isLoading: isCreatingLink,
    isError: creatingLinkFailed,
  } = useFileTemporaryLinkMutation();
  const {
    updateDocumentDate,
    error: updateDocumentDateError,
    reset: resetUpdateDocumentDate,
    isLoading: isUpdatingDocumentDate,
  } = useUpdateFileDocumentDate();
  const [fileToEdit, setFileToEdit] = useState<{
    dataUploadFileId: string;
    documentDate: Date | null;
    isResolved: boolean;
    parentId: string | undefined;
    isFiled: boolean;
  } | null>(null);

  const handleDeleteFile = async (file: EntityWithFile) => {
    try {
      await deleteFile(file.id);
    } catch (error) {
      notifyAndLogError(
        'Failed to delete file',
        'An error occurred while trying to delete the file. Please try again later.',
        error,
        { clientId, fileId: file.id },
      );
    }
  };

  const handleEditGeneratedDate = (file: UploadedFile) => {
    resetUpdateDocumentDate();
    setFileToEdit({
      dataUploadFileId: file.id,
      documentDate: file.documentDate ? new Date(file.documentDate) : null,
      isResolved: file.isResolved,
      parentId: file.parentId,
      isFiled: file.file.isFiled,
    });
  };

  const handleEditDateConfirm = async (date: Date) => {
    if (!fileToEdit) return;
    try {
      await updateDocumentDate(
        clientId,
        fileToEdit.dataUploadFileId,
        date,
        fileToEdit.isResolved,
        fileToEdit.parentId,
        fileToEdit.isFiled,
      );
      setFileToEdit(null);
      await refetchFiles();
    } catch (error) {
      notifyAndLogError(
        'Failed to update date',
        'An error occurred while trying to update the date. Please try again later.',
        error,
        { clientId, dataUploadFileId: fileToEdit.dataUploadFileId },
      );
    }
  };

  const handleSetAsResolved = async (file: UploadedFile, isResolved: boolean = true) => {
    try {
      Mixpanel.track(MixpanelEvents.DATA_UPLOAD_FILE_SET_RESOLVED, dataUploadFileProperties(file));
      await setFileResolved(file, isResolved);
    } catch (error) {
      notifyAndLogError(
        'Failed to mark file as reviewed',
        'An error occurred while trying to mark the file as reviewed. Please try again later.',
        error,
        { clientId, fileId: file.id },
      );
    }
  };

  const audioPlayer = useDocumentAudioPlayer();

  const handleViewFile = async (uploadedFile: UploadedFile, anchor?: HTMLAnchorElement) => {
    try {
      if (anchor) {
        const link = await createLink(uploadedFile.file.id);
        anchor.href = link.url;
        anchor.download = uploadedFile.file.fileName || 'download';
        anchor.target = '_blank';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
      } else {
        const link = await createLink(uploadedFile.file.id);
        window.open(link.url, '_blank');
      }
    } catch (error) {
      notifyAndLogError(
        'Failed to open file',
        'An error occurred while trying to open the file. Please try again later.',
        error,
        { clientId, fileId: uploadedFile.file.id },
      );
    }
  };

  const addPendingFiles = (fileNames: { name: string; size: number; type: string }[]) => {
    const newFiles: UploadedFile[] = fileNames.map(f => ({
      id: `pending-${uuidv4()}`,
      file: {
        id: 0,
        fileName: f.name,
        fileSize: f.size,
        fileType: f.type,
        isFolder: false,
        isResolved: false,
        isFiled: false,
        isTemporary: false,
        createdAt: new Date().toISOString(),
      },
      isFolder: false,
      isResolved: false,
      createdAt: new Date().toISOString(),
      detectedClients: [],
      backgroundProcess: {
        id: '',
        processName: '',
        status: 'PENDING' as const,
        statusMessage: '',
        extraDetails: null,
      },
    }));
    setPendingFiles(prev => [...prev, ...newFiles]);
    return newFiles;
  };

  const uploadWithOptimisticUI = <T,>(
    items: T[],
    toPendingInfo: (item: T) => { name: string; size: number; type: string },
    doUpload: () => Promise<UploadedFile[]>,
  ) => {
    const pending = addPendingFiles(items.map(toPendingInfo));
    const pendingIds = new Set(pending.map(f => f.id));
    doUpload()
      .then(uploadedFiles => {
        uploadedFiles.forEach(file => {
          Mixpanel.track(MixpanelEvents.DATA_UPLOAD_FILE_UPLOADED, dataUploadFileProperties(file));
        });
        dispatchAppEvent('DATA_UPLOADED');
      })
      .catch(error => {
        notifyAndLogError(
          'Failed to upload files',
          'An error occurred while uploading the files. Please try again later.',
          error,
          { clientId, fileCount: items.length },
        );
      })
      .finally(() => {
        refetchFiles()
          .catch(() => {})
          .finally(() => {
            setPendingFiles(prev => prev.filter(f => !pendingIds.has(f.id)));
          });
      });
  };

  const uploadFilesDirectly = (files: File[]) => {
    uploadWithOptimisticUI(
      files,
      f => ({ name: f.name, size: f.size, type: f.type }),
      () =>
        uploadFiles(files.map(file => ({ file, modelId: undefined, documentDate: null }))).then(
          results => results.map(r => r.data),
        ),
    );
  };

  const uploadOneDriveFilesDirectly = (items: PickedItem[]) => {
    uploadWithOptimisticUI(
      items,
      i => ({ name: i.name || 'Unknown', size: i.size || 0, type: '' }),
      () => uploadOneDriveFiles({ items, documentDate: null, parentId: null }),
    );
  };

  const handleOneDriveClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();

    openPicker(handlePickedOneDriveItems, extensions);
  };

  const handleFileDocument = (file: UploadedFile) => {
    setSelectedFileForMove(file);
    openMoveModal();
  };

  const handleMoveFile = async (file: UploadedFile, parentId: string | null | undefined) => {
    try {
      await moveFile(file, parentId);
      Mixpanel.track(MixpanelEvents.DATA_UPLOAD_FILE_DOCUMENT_FILED, {
        ...dataUploadFileProperties(file),
        parentId,
      });
    } catch (error) {
      notifyAndLogError(
        'Failed to move file',
        'An error occurred while trying to move the file. Please try again later.',
        error,
        { clientId, fileId: file.id, parentId },
      );
    }
  };

  const allFiles = useMemo(() => [...filesToAdd, ...pendingFiles], [filesToAdd, pendingFiles]);

  const filteredFiles = useMemo(() => {
    if (!searchValue) return allFiles;
    return allFiles.filter(file =>
      file.file.fileName.toLowerCase().includes(searchValue.toLowerCase()),
    );
  }, [allFiles, searchValue]);

  const getPrimaryAction = (file: UploadedFile) => {
    if (!file.isResolved) {
      return {
        type: 'link' as const,
        label: 'Process data',
        variant: 'primary' as const,
        icon: IconArrowRight,
        onClick: () => {
          Mixpanel.track(
            MixpanelEvents.DATA_UPLOAD_FILE_PROCESS_CLICK,
            dataUploadFileProperties(file),
          );
        },
        getLink: (file: UploadedFile) =>
          `/client/${clientId}/detected-entities?fileId=${file.id}&fileName=${file.file.fileName}&guid=${uuidv4()}`,
        condition: (file: UploadedFile) =>
          !file.backgroundProcess || file.backgroundProcess.status === 'SUCCESS',
      };
    }

    if (file.isResolved && !file.file.isFiled) {
      return {
        type: 'action' as const,
        label: 'File document',
        variant: 'secondary' as const,
        icon: IconFolder,
        onClick: handleFileDocument,
      };
    }

    return undefined;
  };

  if (error || creatingLinkFailed) {
    return <GenericErrorPage error={error || { creatingLinkFailed }} />;
  }

  if (isCreatingLink || isLoading) {
    return <PageLoader absolute={false} />;
  }

  const handlePickedOneDriveItems = (items: PickedItem[]) => {
    Logger.info('picked items from OneDrive:', { items });
    uploadOneDriveFilesDirectly(items);
  };

  return (
    <>
      {allFiles.length === 0 ? (
        <FileUploadLayout title="Add documents">
          <EmptyView
            title="Keep all documents securely and in one place"
            subtitle="Siesta AI will scan and look for data that you use to populate the fields on the system. Documents can be used for Siesta AI reports either for context and/or as information for including."
            ctaText="Upload new"
            ctaIcon={IconPlus}
            customCta={
              <Stack gap="xxs">
                <FileButton onChange={uploadFilesDirectly} multiple accept={fileButtonAccept}>
                  {props => (
                    <MantineButton {...props} variant="primary" size="md">
                      Upload new
                    </MantineButton>
                  )}
                </FileButton>
                <Button onClick={handleOneDriveClick} variant="secondary" size="md">
                  Upload from SharePoint
                </Button>
              </Stack>
            }
          />
        </FileUploadLayout>
      ) : (
        <FileUploadLayout
          title="Add documents"
          showSearch
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          searchResultCount={filteredFiles.length}
        >
          <Stack gap="xxl">
            {!searchValue && (
              <DropZone
                onFilesSelected={uploadFilesDirectly}
                onOneDriveClick={handleOneDriveClick}
                documentType={documentTypeChoice}
              />
            )}

            <FilesTable<UploadedFile>
              entitiesWithFile={filteredFiles}
              isLoading={isLoading}
              primaryAction={getPrimaryAction}
              additionalColumns={[
                {
                  label: 'Document date',
                  valueBuilder: f => (
                    <Group gap="xs" wrap="nowrap">
                      <Text fw={400} size="sm" c={f.documentDate ? undefined : 'gray.5'}>
                        {f.documentDate ? formatDate(f.documentDate) : 'No date detected'}
                      </Text>
                      {!isPendingFile(f) && (
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          onClick={() => handleEditGeneratedDate(f)}
                        >
                          <IconPencil size={14} />
                        </ActionIcon>
                      )}
                    </Group>
                  ),
                },
                {
                  label: 'Document type',
                  valueBuilder: f => (
                    <Text fw={400} size="sm">
                      {getFileTypeLabel(f)}
                    </Text>
                  ),
                },
                {
                  label: 'Status',
                  textAlign: 'center',
                  valueBuilder: f => {
                    if (f.isResolved && !f.file.isFiled) {
                      return <FileStatusIcon status="UNFILED" />;
                    }
                    if (!f.backgroundProcess?.status) {
                      return <></>;
                    }
                    if (f.backgroundProcess.status === 'FAILED') {
                      const errorMessage =
                        (f.backgroundProcess.extraDetails?.exc_info as string) ||
                        'Processing failed';
                      return <FileStatusIcon status="FAILED" tooltipLabel={errorMessage} />;
                    }
                    return <FileStatusIcon status={f.backgroundProcess.status} />;
                  },
                },
              ]}
              actions={(file: UploadedFile) => {
                if (isPendingFile(file)) return [];

                const fileIsAudio = isAudioFile(file.file.fileType, file.file.fileName);
                const topActions = fileIsAudio
                  ? [
                      {
                        label: 'Download',
                        action: (f: UploadedFile) => audioPlayer.download(f, clientId),
                      },
                      {
                        label: 'Play',
                        action: (f: UploadedFile) => audioPlayer.play(f.file.id, f.file.fileName),
                      },
                      { type: 'divider' as const },
                    ]
                  : [
                      { label: 'Download document', action: handleViewFile },
                      { type: 'divider' as const },
                    ];

                if (file.isResolved) {
                  return [
                    ...topActions,
                    {
                      label: 'Set as not reviewed',
                      action: file => handleSetAsResolved(file, false),
                    },
                    { label: 'Delete', action: handleDeleteFile },
                  ];
                } else {
                  return [
                    ...topActions,
                    { label: 'Set as reviewed', action: file => handleSetAsResolved(file, true) },
                    { label: 'Delete', action: handleDeleteFile },
                  ];
                }
              }}
            />
          </Stack>
        </FileUploadLayout>
      )}

      {selectedFileForMove && (
        <MoveFileModal
          opened={moveModalOpened}
          close={closeMoveModal}
          file={selectedFileForMove}
          folders={files}
          rootLabel={`All ${client?.firstName || 'client'}'s Documents`}
          onFileMoved={handleMoveFile}
        />
      )}

      <EditDateModal
        opened={!!fileToEdit}
        onClose={() => setFileToEdit(null)}
        onConfirm={handleEditDateConfirm}
        title="Edit document date"
        initialDate={fileToEdit?.documentDate ?? null}
        error={updateDocumentDateError}
        isLoading={isUpdatingDocumentDate}
      />

      {audioPlayer.isVisible && (
        <AudioPlayer
          isPlaying={audioPlayer.isPlaying}
          currentTime={audioPlayer.currentTime}
          duration={audioPlayer.duration}
          isMuted={audioPlayer.isMuted}
          onPlayPause={audioPlayer.handlePlayPause}
          onSeek={audioPlayer.handleSeek}
          onMuteToggle={audioPlayer.handleMuteToggle}
          fileName={audioPlayer.fileName}
          onClose={audioPlayer.handleClose}
        />
      )}
    </>
  );
}
